/**
 * Hook for QMS AI Investigation
 *
 * Provides queries and mutations for AI-powered ticket investigation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchQmsAiInvestigation,
  retryQmsAiInvestigation,
  triggerQmsAiInvestigation,
  acceptQmsAiFindings,
} from '../api/qmsApi';
import { qmsKeys } from './useQmsBoard';
import type { QmsAiInvestigation } from '../types/qms.types';

// Query key factory
export const aiInvestigationKeys = {
  all: ['qms', 'ai-investigation'] as const,
  byTicket: (ticketId: string) => [...aiInvestigationKeys.all, ticketId] as const,
};

/**
 * Fetch AI investigation for a ticket
 * Automatically polls while investigation is in progress
 */
export function useQmsAiInvestigation(ticketId: string | null | undefined) {
  return useQuery({
    queryKey: ticketId ? aiInvestigationKeys.byTicket(ticketId) : ['qms', 'ai-investigation', 'none'],
    queryFn: () => fetchQmsAiInvestigation(ticketId!),
    enabled: !!ticketId,
    // Poll every 5 seconds while investigation is in progress
    refetchInterval: (query) => {
      const data = query.state.data as QmsAiInvestigation | null | undefined;
      if (data?.status === 'pending' || data?.status === 'in_progress'
        || data?.findings?.codeFix?.status === 'pending' || data?.findings?.codeFix?.status === 'in_progress') {
        return 5000;
      }
      return false;
    },
    // Refetch on window focus only if not actively polling
    refetchOnWindowFocus: (query) => {
      const data = query.state.data as QmsAiInvestigation | null | undefined;
      return data?.status !== 'pending' && data?.status !== 'in_progress';
    },
  });
}

/**
 * Retry a failed AI investigation
 */
export function useRetryAiInvestigation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketId: string) => retryQmsAiInvestigation(ticketId),
    onSuccess: (_, ticketId) => {
      // Invalidate investigation query to trigger refetch
      queryClient.invalidateQueries({
        queryKey: aiInvestigationKeys.byTicket(ticketId),
      });
      // Also invalidate ticket queries to update status
      queryClient.invalidateQueries({
        queryKey: qmsKeys.ticket(ticketId),
      });
    },
  });
}

/**
 * Trigger a new AI investigation for a ticket
 */
export function useTriggerAiInvestigation(rerun = false) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketId: string) => triggerQmsAiInvestigation(ticketId, rerun),
    onSuccess: (_, ticketId) => {
      // Invalidate investigation query to trigger refetch (will start polling)
      queryClient.invalidateQueries({
        queryKey: aiInvestigationKeys.byTicket(ticketId),
      });
      // Also invalidate ticket queries to update status
      queryClient.invalidateQueries({
        queryKey: qmsKeys.ticket(ticketId),
      });
    },
  });
}

/**
 * Accept AI findings and apply to ticket
 */
export function useAcceptAiFindings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, fields }: { ticketId: string; fields?: string[] }) =>
      acceptQmsAiFindings(ticketId, fields),
    onSuccess: (_, { ticketId }) => {
      // Invalidate ticket query to show updated fields
      queryClient.invalidateQueries({
        queryKey: qmsKeys.ticket(ticketId),
      });
      // Invalidate board to reflect changes
      queryClient.invalidateQueries({
        queryKey: qmsKeys.boards(),
      });
      // Invalidate investigation query
      queryClient.invalidateQueries({
        queryKey: aiInvestigationKeys.byTicket(ticketId),
      });
    },
  });
}

/**
 * Check if AI investigation is available for a ticket
 * Returns true if investigation exists and has findings
 */
export function useHasAiFindings(ticketId: string | null | undefined): boolean {
  const { data: investigation } = useQmsAiInvestigation(ticketId);
  return investigation?.status === 'completed' && !!investigation.findings;
}
